import React, { useCallback } from 'react';
import { Tree } from 'antd';
import { useTheme } from '@/components/common/ThemeProvider';
import {
    EyeOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined
} from '@ant-design/icons';
import type { DataNode, DirectoryTreeProps } from 'antd/es/tree';
import { CoaNode } from '../types';
import '../styles/coa-tree-view.css';

interface CoaTreeViewProps {
    data: CoaNode[];
    onNodeAction: (node: CoaNode, action: 'view' | 'add' | 'edit' | 'delete') => void;
    expandedKeys?: Set<string>;
    onExpand?: (key: string) => void;
}

const { DirectoryTree } = Tree;

export const CoaTreeView: React.FC<CoaTreeViewProps> = ({
                                                            data,
                                                            onNodeAction,
                                                            expandedKeys,
                                                            onExpand,
                                                        }) => {
    const { dark } = useTheme();

    // Convert CoaNode[] to DataNode[] for antd Tree
    const convertToTreeData = useCallback((nodes: CoaNode[]): DataNode[] => {
        console.log(nodes);
        return nodes.map(node => ({
            key: node.glNo,
            title: (
                <div className="flex items-center justify-between group">
                    <span>{`${node.glNo} - ${node.acName}`}</span>
                    <div className={`invisible group-hover:visible flex items-center gap-2 ${dark ? 'text-gray-300' : 'text-gray-600'}`}>
                        <EyeOutlined
                            className="cursor-pointer hover:text-blue-500"
                            onClick={(e) => {
                                e.stopPropagation();
                                onNodeAction(node, 'view');
                            }}
                        />
                        {node.canAddChild && (
                            <PlusOutlined
                                className="cursor-pointer hover:text-green-500"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onNodeAction(node, 'add');
                                }}
                            />
                        )}
                        <EditOutlined
                            className="cursor-pointer hover:text-yellow-500"
                            onClick={(e) => {
                                e.stopPropagation();
                                onNodeAction(node, 'edit');
                            }}
                        />
                        {node.canDelete && (
                            <DeleteOutlined
                                className="cursor-pointer hover:text-red-500"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onNodeAction(node, 'delete');
                                }}
                            />
                        )}
                    </div>
                </div>
            ),
            children: node.children ? convertToTreeData(node.children) : undefined,
            isLeaf: !node.children || node.children.length === 0,
        }));
    }, [dark, onNodeAction]);

    const treeData = React.useMemo(() => convertToTreeData(data), [data, convertToTreeData]);

    console.log(treeData);

    const handleExpand: DirectoryTreeProps['onExpand'] = (expandedKeysValue, info) => {
        if (onExpand && info.node.key) {
            onExpand(info.node.key.toString());
        }
    };

    return (
        <div className={`h-[calc(100vh-200px)] overflow-auto ${dark ? 'bg-gray-800 text-white' : 'bg-white'}`}>
            <DirectoryTree
                className={dark ? 'ant-tree-dark' : ''}
                treeData={treeData}
                expandedKeys={expandedKeys ? Array.from(expandedKeys) : undefined}
                onExpand={handleExpand}
                showIcon={false}
                showLine={{ showLeafIcon: false }}
            />
        </div>
    );
};